import { autoinject } from "aurelia-framework";
import { FetchClient } from "../common/fetch-client";

import {
    ApplicationEnum,
    EvvStatusHomeCareEnum,
    EvvStatusHomeHealthEnum,
    GenderIdentityEnum,
    PatientStatusEnum
} from "../enums/enums";
import type {
    IGetPatientsByNameResponse,
    IGetPatientByBranchRequest,
    IGetPatientByBranchResponse,
    IPatientRequest,
    IPatientByAgencyRequest,
    IPatientCountPayload,
    IPatientCountResponse,
    IHCPatientResponse,
    INonAdmitHcResponse,
    ICompletedPatientChartsResponseArray,
    IChartStatusResponse,
    ISlimResponse,
    IGetPatientProfile,
    IPatientEpisodeResponse
} from "../interfaces/i-patient-service";
import type { IPatientPayersRequest, IPatientPayersResponse } from "../resources-vue/vue-interfaces/i-task-details";
import type { IPaginationNew as ImportedIPagination } from "../interfaces/i-pagination";
import type {
    IPatient,
    IPatientRequestParams,
    IPatientValidation,
    IPatientValidationResponse
} from "../interfaces/patient.interface";
import type {
    IGetPatientDme,
    IAddPatientDme,
    IDeletePatientDme,
    IDmeResponse
} from "../resources-vue/vue-interfaces/i-patient-dme";
import { DmeNeeded } from "./../models/dme-needed";
import type {
    ICurrentBenefitPeriodRequest,
    ICurrentBenefitPeriodResponse
} from "../resources-vue/vue-interfaces/i-patient-task-hos-pal";
import { HomeHealthPatient } from "../resources-vue/vue-interfaces/i-homeHealth-patient";
import { IImplantableDevice, INonAdmitPatientQuery } from "../resources-vue/vue-interfaces/i-get-referral";
import { INonAdmitHHPatientQuery } from "../interfaces/i-get-referral";
import { IOpenOrder } from "../resources-vue/vue-interfaces/i-plan-of-care";
import {
    IPalliativeConvertToPending,
    ICreatePatientNoteRequest
} from "../resources-vue/vue-interfaces/i-palliative-convert-to-pending";
import {
    IFamilyHealthHistory,
    IHealthCondition,
    IHospitalization,
    IPatientHealthHistory
} from "../resources-vue/vue-interfaces/i-health";
import { IPaginationRequest } from "../resources-vue/vue-interfaces/i-pagination";
import { IGetPatientAdmissionPeriods, IGetPatientsSlimInfoResult } from "../resources-vue/vue-interfaces/i-patient";
import { IPalHealthHistoryDiagnosis } from "../resources-vue/vue-interfaces/i-diagnosis";
import {
    IGetPatientDocumentsQuery,
    IPatientDocument,
    IUploadPatientDocumentQuery,
    IUploadPatientDocumentResult
} from "../resources-vue/vue-interfaces/i-get-patient";
import { ServiceLocation } from "../resources-vue/vue-models/service-locations";
import { IEvvDetailsHomeHealthResp, IEvvTaskDetailsHHPayload } from "../resources-vue/vue-interfaces/i-visit-evv-hh";
import { HospicePatientPayer } from "../resources-vue/vue-models/hospice-patient-payer";
import { IGetCurrentPatientPayerQuery, IGetPatientPayerQuery } from "../resources-vue/vue-interfaces/i-patient-payor";
import { HospiceConvertToPendingPatient } from "../resources-vue/vue-models/hospice-convertToPending-details";
import { AuthorizedContact } from "../resources-vue/vue-models/authorized-contact";
import { ITaskByPatientResponse } from "./task.service";

@autoinject
export class PatientService {
    private _fetchClient: FetchClient;
    private _baseUrl: string = "/api/v1/patients";
    private _baseUrl2: string = "/api/v1/patient";
    private _baseUrl3: string = "/api/v1";

    public constructor(fetchClient: FetchClient) {
        this._fetchClient = fetchClient;
    }

    public async findAll(params: IPatientRequestParams) {
        return await this._fetchClient
            .build(this._baseUrl)
            .useQueryString(params)
            .fetch<ImportedIPagination<IPatient>>();
    }

    public async findPatientsForCalendar(options: IPatientCalendarRequestParams) {
        return await this._fetchClient
            .build(`/api/v1/patients-for-calendar`)
            .useMethod("POST")
            .useBody(options)
            .fetch<IPaginationNew<IPatientCalendarResponse>>();
    }

    // Patients Dme End Points
    public async getPatientsDme(params: IGetPatientDme) {
        return await this._fetchClient
            .build(`${this._baseUrl}/get-patient-DME`)
            .useQueryString(params)
            .fetch<DmeNeeded[]>();
    }

    public async addPatientsDme(dme: IAddPatientDme) {
        return await this._fetchClient
            .build(`${this._baseUrl}/add-patient-DME`)
            .useMethod("POST")
            .useBody(dme)
            .fetch<IDmeResponse>();
    }

    public async updatePatientsDme(dmeInfo: DmeNeeded) {
        return await this._fetchClient
            .build(`${this._baseUrl}/update-patient-DME`)
            .useMethod("PUT")
            .useBody(dmeInfo)
            .fetch<IDmeResponse>();
    }

    public async deletePatientsDme(payload: IDeletePatientDme) {
        return await this._fetchClient
            .build(`${this._baseUrl}/delete-patient-DME`)
            .useMethod("DELETE")
            .useBody(payload)
            .fetch<IDmeResponse>();
    }

    public async getPatientsByName(payload: IGetPatientsByNameQuery) {
        return await this._fetchClient
            .build(`${this._baseUrl}/by-name`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IGetPatientsByNameResponse>();
    }

    public async getPayersByPatientId(payload: IPatientPayersRequest) {
        const queryParam = { taskId: payload.taskId, accountId: payload.accountId, applications: payload.applications };
        return await this._fetchClient
            .build(`/api/v1/${payload.patientId}/payers/by-visit`)
            .useQueryString(queryParam)
            .fetch<IPatientPayersResponse>();
    }

    public async getPatientByBranch(payload: IGetPatientByBranchRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/by-branch`)
            .useQueryString(payload)
            .fetch<IGetPatientByBranchResponse[]>();
    }

    public async checkDuplicates(payload: IPatientValidation) {
        return await this._fetchClient
            .build(`${this._baseUrl}/duplicate-check`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IPatientValidationResponse>();
    }

    public async getPatients(payload: IPatientRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/lean`)
            .useQueryString(payload)
            .fetch<IGetPatientsByNameResponse>();
    }

    public async getCurrentBenefitPeriod(payload: ICurrentBenefitPeriodRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${payload.patientId}/slim`)
            .useQueryString(payload)
            .fetch<ICurrentBenefitPeriodResponse>();
    }
    public async getCurrentEpisode(payload: ICurrentEpisodeRequestParams) {
        return await this._fetchClient
            .build(`${this._baseUrl}/episode/current`)
            .useQueryString(payload)
            .fetch<ICurrentEpisodeResponse>();
    }

    public async getPatientsByAgency(payload: IPatientByAgencyRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/by-agency`)
            .useQueryString(payload)
            .fetch<IGetPatientByBranchResponse[]>();
    }

    public async getHospicePatientById(payload: IGetHospicePatientData) {
        return await this._fetchClient
            .build(`${this._baseUrl}/hospice/${payload.patientId}`)
            .useQueryString(payload)
            .fetch<any>();
    }

    public async getHCPatientById(payload: IGetHCPatientData) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/hcpatients/data`)
            .useQueryString(payload)
            .fetch<IHCPatientResponse>();
    }

    public async getEditHHReferral(payload: IGetEditHHReferral) {
        return await this._fetchClient.build(`${this._baseUrl2}/referral/data`).useQueryString(payload).fetch<any>();
    }
    public async getverifyEligibility(payload: IGetVerifyEligibility) {
        return await this._fetchClient.build(`${this._baseUrl}/verifyeligibility`).useQueryString(payload).fetch<any>();
    }

    public async downloadEligibility(payload: IDownloadEligibilityPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl}/downloadmedicareeligibility`)
            .useMethod("POST")
            .useBody(payload)
            .fetchBlob();
    }

    public async getHHPatientAllData(payload: IGetHHPatientAllDataParam) {
        return await this._fetchClient
            .build(`${this._baseUrl}/data`)
            .useQueryString(payload)
            .fetch<HomeHealthPatient>();
    }

    public async getReadmitHHPatientData(payload: IGetHHPatientAllDataParam) {
        return await this._fetchClient
            .build(`${this._baseUrl}/edit-patient-readmit`)
            .useQueryString(payload)
            .fetch<HomeHealthPatient>();
    }

    public async reAdmitPendingHH(payload: IGetEditHHReferral) {
        return await this._fetchClient
            .build(`${this._baseUrl}/readmit-patient`)
            .useMethod("POST")
            .useBody(payload)
            .fetch();
    }

    public async admitNewPatient(payload: IGetEditHHReferral) {
        return await this._fetchClient
            .build(`${this._baseUrl}/admit-new-patient`)
            .useMethod("POST")
            .useBody(payload)
            .fetch();
    }

    public async saveNonAdmitPatinetHH(payload: IGetEditHHReferral) {
        return await this._fetchClient
            .build(`${this._baseUrl}/save-readmit-patient`)
            .useMethod("POST")
            .useBody(payload)
            .fetch();
    }

    public async convertToPendingHH(payload: IGetEditHHReferral) {
        return await this._fetchClient
            .build(`${this._baseUrl}/convert-to-pending`)
            .useMethod("POST")
            .useBody(payload)
            .fetch();
    }

    public async getPatientsProfile(payload: IPatientProfileParams) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/additional/info`)
            .useQueryString(payload)
            .fetch<IGetPatientProfile>();
    }

    public async updatePatientToNonAdmit(id: string, query: INonAdmitPatientQuery | INonAdmitHHPatientQuery) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${id}/status/non-admit`)
            .useMethod("PUT")
            .useBody(query)
            .fetchNoContent();
    }

    public async updatePatientToNonAdmitHC(id: string, query: any) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${id}/status/non-admit-hc`)
            .useMethod("PUT")
            .useBody(query)
            .fetch<INonAdmitHcResponse>();
    }

    public async patientDiagnosis(payload: IPatientDiagnosis) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/referrals/PdgmIntakeModeling/Diagnoses`)
            .useQueryString(payload)
            .fetch();
    }

    public async getPatientsCount(payload: IPatientCountPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/count`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IPatientCountResponse>();
    }

    public async getConvertPalliativeReferral(payload: IPalliativeReferralQuery) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/get-patient-tab`)
            .useQueryString(payload)
            .fetch<IPalliativeConvertToPending>();
    }

    public async updateConvertPalliativeReferral(payload: IPalliativeConvertToPending) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/update-patient-tab`)
            .useMethod("PUT")
            .useBody(payload)
            .fetch<IPatientCountResponse>();
    }

    public async createFamilyHealthHistory(patientId: string, familyHealthHistory: IEditFamilyHealthHistoryPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/family-health-history`)
            .useBody(familyHealthHistory)
            .useMethod("POST")
            .fetch();
    }

    public async getFamilyHealthHistory(patientId: string, query: IPaginationRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/family-health-history`)
            .useMethod("GET")
            .useQueryString(query)
            .fetch();
    }

    public async getHealthCondition(query: any) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/search-health-condition`)
            .useQueryString(query)
            .fetch<IHealthCondition>();
    }

    public async updateFamilyHealthHistory(patientId: string, familyHealthHistory: IEditFamilyHealthHistoryPayload) {
        return await this._fetchClient
            .build(
                `${this._baseUrl2}/${patientId}/family-health-history/${familyHealthHistory.PalliativePatientFamilyHealthHistory.Id}`
            )
            .useBody(familyHealthHistory)
            .useMethod("PUT")
            .fetch();
    }

    public async deleteFamilyHealthHistory(patientId: string, familyHistoryId: string, query: any) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/family-health-history/${familyHistoryId}`)
            .useMethod("DELETE")
            .useQueryString(query)
            .fetch();
    }

    public async getPatientHealthHistory(patientId: string) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${patientId}/health-history`)
            .fetch<IPatientHealthHistory>();
    }

    public async updateHealthHistory(patientId: string, healthHistory: IPatientHealthHistory) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${patientId}/health-history-tab`)
            .useBody(healthHistory)
            .useMethod("PUT")
            .fetch();
    }

    public async updateHospitalization(patientId: string, payload: any) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/hospitalization-history`)
            .useBody(payload)
            .useMethod("PUT")
            .fetch();
    }

    public async deleteHospitalization(patientId: string, query: any) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/hospitalization-history`)
            .useQueryString(query)
            .useMethod("DELETE")
            .fetch();
    }

    public async getPatientSlim(patientId: string, query: any) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${patientId}/slim`)
            .useQueryString(query)
            .fetch<{ data: IGetPatientsSlimInfoResult }>();
    }

    public async getHospitalizationsList(patientId: string, query: IGetHospitalizationHistoryQuery) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/hospitalization-history`)
            .useQueryString(query)
            .fetch<{ data: IPaginationNew<IHospitalization> }>();
    }

    public async updatePatientStatusToHospitalized(patientId: string, query: any) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${patientId}/status/hospitalize`)
            .useMethod("PUT")
            .useBody(query)
            .fetchNoContent();
    }

    public async createHospitalization(patientId: string, patientHospitalizationHistories: any) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/hospitalization-history`)
            .useBody(patientHospitalizationHistories)
            .useMethod("POST")
            .fetch();
    }

    public async validateImplantableDevice(payload: any) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/validate-implantable-device`)
            .useQueryString(payload)
            .fetch();
    }

    public async createImplantableDevice(patientId: string, payload: IImplantableDevice) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/implantable-device`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<ICreateResponse>();
    }

    public async updateImplantableDevice(patientId: string, id: string, payload: IUpdateImplantableDevice) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/implantable-device/${id}`)
            .useMethod("PUT")
            .useBody(payload)
            .fetchNoContent();
    }

    public async updateImplatableDeviceStatus(id: string, payload: any) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${id}/update-implantable-device-status`)
            .useMethod("PUT")
            .useBody(payload)
            .fetch();
    }

    public async deleteImplantableDevice(id: string, payload: any) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/implantable-device/${id}`)
            .useMethod("DELETE")
            .useBody(payload)
            .fetch();
    }

    public async getImplantableDevices(patientId: string, query: IGetImplantableDeviceQuery) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/implantable-devices`)
            .useQueryString(query)
            .fetch<IPaginationNew<IGetImplantableDeviceResponse>>();
    }

    public async getPatientAdmissionPeriodsNoPermission(patientId: string, query: any) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${patientId}/admissionperiods-lookup`)
            .useQueryString(query)
            .fetch<IGetPatientAdmissionPeriods[]>();
    }

    public async getPatientDocuments(patientId: string, query: IGetPatientDocumentsQuery) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/documents`)
            .useQueryString(query)
            .fetch<IPatientDocument[]>();
    }

    public async getPatientDocumentsByType(patientId: string, uploadType: string, query: any) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/documents/${uploadType}`)
            .useMethod("GET")
            .useQueryString(query)
            .fetch();
    }

    public async uploadDocument(patientId: string, query: IUploadPatientDocumentQuery) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/documents`)
            .useMethod("POST")
            .useBody(query)
            .fetch<IUploadPatientDocumentResult>();
    }

    public async deleteDocument(patientId: string, documentId: string) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/documents/${documentId}`)
            .useMethod("DELETE")
            .fetchNoContent();
    }
    public async generatePatientCharts(payload: IGeneratePatientChart) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/export/patient/chart`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IGeneratePatientChart>();
    }

    public async getStatusPatientCharts(payload: IGetPatientChartStatus) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/export/patient/chart/status`)
            .useQueryString(payload)
            .fetch<IChartStatusResponse>();
    }

    public async getCompletedPatientCharts(payload: IGetCompletedPatientChart) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/export/completed/patient/chart`)
            .useQueryString(payload)
            .fetch<ICompletedPatientChartsResponseArray>();
    }
    public async getOpenOrders(patientId: string, payload: IGetOpenOrder) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/openorders`)
            .useQueryString(payload)
            .fetch<IOpenOrder[]>();
    }
    public async getAdmissionInfo(patientId: string, query: IGetBenefitPeriodNumber) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${patientId}/admission-info`)
            .useQueryString(query)
            .fetch<ISlimResponse>();
    }

    public async getSlimData(patientId: string, query: IGetSlimData) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${patientId}/slim`)
            .useQueryString(query)
            .fetch<ISlimResponse>();
    }

    public async loadPatientReferral(id: string, query: ILoadReferralParam) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${id}/load-referral`)
            .useMethod("PUT")
            .useBody(query)
            .fetch();
    }

    public async savePatientDetails(payload: HospiceConvertToPendingPatient) {
        return await this._fetchClient.build(`${this._baseUrl}`).useMethod("PUT").useBody(payload).fetch();
    }

    public async getServiceLocations(patientId: string, payload: any) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${patientId}/servicelocations`)
            .useMethod("GET")
            .useQueryString(payload)
            .fetch();
    }

    public async addServiceLocation(payload: ServiceLocation, patientId: string) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${patientId}/servicelocations`)
            .useMethod("POST")
            .useBody(payload)
            .fetchNoContent();
    }

    public async updateServiceLocation(payload: ServiceLocation, patientId: string, serviceLocationId: string) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${patientId}/servicelocations/${serviceLocationId}`)
            .useMethod("PUT")
            .useBody(payload)
            .fetch();
    }

    public async deleteServiceLocation(payload: any, patientId: string, serviceLocationId: string) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${patientId}/servicelocations/${serviceLocationId}`)
            .useMethod("DELETE")
            .useBody(payload)
            .fetchNoContent();
    }

    public async getLastPatientMrn(payload: ILastPatientMRNPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/last-patient-mrn`)
            .useMethod("GET")
            .useQueryString(payload)
            .fetch<ILastPatientMRNResponse>();
    }

    public async getPatientPayers(queryString: IGetPatientPayerQuery, patientId: string) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${patientId}/payers`)
            .useMethod("GET")
            .useQueryString(queryString)
            .fetch();
    }

    public async getPatientCurrentPayers(queryString: IGetCurrentPatientPayerQuery, patientId: string) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${patientId}/currentpayers`)
            .useMethod("GET")
            .useQueryString(queryString)
            .fetch();
    }

    public async updatePatientPayer(payload: HospicePatientPayer, id: string) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/payers/${id}`)
            .useMethod("PUT")
            .useBody(payload)
            .fetch();
    }

    public async deletePatientPayer(id: string, queryParam: IDeletePatientPayer) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/payers/${id}`)
            .useMethod("DELETE")
            .useBody(queryParam)
            .fetch();
    }

    public async addPatientPayer(patientId: string, payer: HospicePatientPayer) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${patientId}/payers`)
            .useMethod("POST")
            .useBody(payer)
            .fetch();
    }

    public async getPayersByName(patientId: string, queryParam: any) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${patientId}/payers/by-name`)
            .useMethod("GET")
            .useQueryString(queryParam)
            .fetch();
    }

    public async getAuthorizedContacts(queryParam: IGetContactRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/contact`)
            .useMethod("GET")
            .useQueryString(queryParam)
            .fetch();
    }

    public async addAuthorizedContact(referralId: string, contact: AuthorizedContact) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${referralId}/authorized-contact`)
            .useMethod("POST")
            .useBody(contact)
            .fetch();
    }

    public async updateAuthorizedContact(accountId: string, contactInformation: AuthorizedContact) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/authorized-contact/${contactInformation.id}`)
            .useMethod("PUT")
            .useQueryString({ accountId })
            .useBody(contactInformation)
            .fetch();
    }

    public async deleteAuthorizedContact(payload: IDeleteContactRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/authorized-contact/${payload.contactId}`)
            .useMethod("DELETE")
            .useQueryString(payload)
            .fetch();
    }

    public async reorderAuthorizedContacts(payload: IReorderPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/order-contacts`)
            .useMethod("PUT")
            .useBody(payload)
            .fetch();
    }
    public async getPalAuthorizedContact(Id: string, payload: IGetContactRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${Id}/authorized-contact`)
            .useQueryString(payload)
            .fetch<AuthorizedContact[]>();
    }
    public async addPalAuthorizedContact(Id: string, contact: AuthorizedContact) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${Id}/patient-authorized-contact`)
            .useMethod("POST")
            .useBody(contact)
            .fetch();
    }

    public async getVaccineRecords(patientId: string, query: IGetImmunizationList) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/immunizations`)
            .useQueryString(query)
            .fetch<IPaginationNew<IImmunizationRecord>>();
    }

    public async deleteVaccineRecord(patientId: string, immunizationId: string, query: IGetSlimData) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/immunization/${immunizationId}`)
            .useMethod("Delete")
            .useQueryString(query)
            .fetch();
    }

    public async getSearchImmunization(query: ISearchImmunizationRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/search-immunization`)
            .useMethod("GET")
            .useQueryString(query)
            .fetch();
    }

    public async updateVaccineRecord(
        patientId: string,
        immunizationId: string,
        payload: ICreateImmunizationRecordRequest
    ) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/immunization/${immunizationId}`)
            .useMethod("PUT")
            .useBody(payload)
            .fetch();
    }

    public async createVaccineRecord(patientId: string, payload: ICreateImmunizationRecordRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/immunization`)
            .useMethod("POST")
            .useBody(payload)
            .fetch();
    }

    public async downloadImmunization(payload: IGetContactRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/immunizations/xlsx`)
            .useMethod("POST")
            .useBody(payload)
            .fetchBlob();
    }

    public async getBenefitPeriods(payload: IGetHospicePatientData) {
        return await this._fetchClient.build(`${this._baseUrl2}/benefit-periods`).useQueryString(payload).fetch();
    }

    public async reAdmitHCPatient(payload: IReadmitHCPatientRequest) {
        return await this._fetchClient.build(`${this._baseUrl2}/readmit`).useMethod("POST").useBody(payload).fetch();
    }

    public async getEpisodeRange(payload: IPatientProfileParams) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/episode-range-list`)
            .useQueryString(payload)
            .fetch<IPatientEpisodeResponse[]>();
    }

    public async getHHPatientChartDetails(payload: IHHPatientChartDetailsRequest) {
        return await this._fetchClient.build(`${this._baseUrl}/chart/data`).useQueryString(payload).fetch();
    }
    public async getEvvTaskDetailsHH(payload: IEvvTaskDetailsHHPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl}/evv-verification/info`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IEvvDetailsHomeHealthResp>();
    }

    public async getPatientNotes(patientId: string, query: ICreatePatientNoteRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl2}/${patientId}/patient-notes`)
            .useMethod("GET")
            .useQueryString(query)
            .fetch();
    }

    public async createPatientNote(patientId: string, payload: ICreatePatientNoteRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl3}/${patientId}/patientnotes`)
            .useMethod("POST")
            .useBody(payload)
            .fetch();
    }
    public async getNearByClients(payload: INearbyClientsPayload) {
        return await this._fetchClient
            .build(`${this._baseUrl}/find-nearby-clients`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<INearbyClientsRes>();
    }
}

export interface IHHPatientChartDetailsRequest {
    agencyId: string;
    patientId: string;
    application: number;
}
export interface IReadmitHCPatientRequest {
    AgencyId: string;
    AgencyLocationId: string;
    PatientId: string;
    Application: number;
    ReferralDate: string;
    ReadmissionDate: string;
    ReAdmissionHour: string;
    CaseManagerId: string;
    ClinicianId: string;
    PhysicianId: string;
}

export interface IUpdateImplantableDevice {
    Application: ApplicationEnum;
    AccountId: string;
    PatientId: string;
}
export interface IUpdateImplantableDevice {
    Application: ApplicationEnum;
    AccountId: string;
    DateOfImplant: string;
}

export interface IGetImplantableDeviceResponse {
    Id?: string;
    Udi: string;
    Name: string;
    Description: string;
    VersionModelNumber: string;
    CompanyName: string;
    LotBatchNumber: string;
    BrandName: string;
    SerialNumber: string;
    ExpirationDate: string;
    ManufacturingDate: string;
    SafetyInformation: string;
    IssuingAgency: string;
    HasNaturalRubber: boolean;
    IsHumanOrIssueProduct: boolean;
    DateOfImplant: string;
    DeviceStatus: number;
}
export interface IGetImplantableDeviceQuery {
    deviceStatus: number;
    sortBy: string;
    order: string;
    application: ApplicationEnum;
    accountId: string;
    pageLength: number;
    page: number;
}
export interface ICreateImmunizationRecordRequest {
    Application: ApplicationEnum;
    AccountId: string;
    PalliativePatientImmunization: IImmunizationRecord;
}

export interface ISearchImmunizationRequest {
    application: ApplicationEnum;
    searchTerm: string;
}
export interface IImmunizationRecord {
    Id: string;
    PatientId: string;
    ProductName: string;
    Description: string;
    IsVaccineUnknown: boolean;
    CvxCode: string;
    Manufacturer: string;
    LotNumber: string;
    MvxCode: string;
    DateOfVaccination: string;
    DateOfDocumentation: string;
    Status: number;
    TypeOfRecord: number;
    ReasonNotPerformed: string;
    ReasonNotPerformedDisplayName: string;
    Comments: string;
    AdministeredBy: string;
}

export interface IGetImmunizationList {
    accountId: string;
    application: ApplicationEnum;
    id: string;
    page: number;
    pageLength: number;
}

export interface IGetSlimData {
    agencyId: string;
    application: ApplicationEnum;
}
export interface IGetBenefitPeriodNumber {
    accountId: string;
    application: ApplicationEnum;
}
export interface IPalliativeReferralQuery {
    agencyId: string;
    patientId: string;
    application: ApplicationEnum;
    tabName: string;
}

export interface IEditFamilyHealthHistoryPayload {
    Application: number;
    AccountId: string;
    PalliativePatientFamilyHealthHistory: IFamilyHealthHistory;
}
export interface IGetHospitalizationHistoryQuery extends IPaginationRequest {
    statusUpdateOnly?: boolean;
}

export interface IHospitalizedPatientQuery {
    AdmissionDate: string;
    FacilitySourceId: string;
    FacilitySourceName: string;
    FacilityContactName: string;
    CommunityLiaisonId: string;
    Comments: string;
    HospitalUnit: string;
    IsPlanned: boolean;
    IsPreventable: boolean;
    HospitalizationHistoryDiagnoses: IPalHealthHistoryDiagnosis[];
    CommunityLiaisonFirstName: string;
    CommunityLiaisonLastName: string;
    DischargeDate?: string;
    DischargedTo?: string;
}

export interface IReorderPayload {
    Application: ApplicationEnum;
    AccountId: string;
    ContactsOrder: {
        ContactId: string;
        ContactOrder: number;
    }[];
}

export interface IDeletePatientPayer {
    AccountId: string;
    Application: ApplicationEnum;
    Id: string;
}

export interface IGetContactRequest {
    accountId: string;
    application: ApplicationEnum;
    patientId: string;
}

export interface IDeleteContactRequest {
    accountId: string;
    application: ApplicationEnum;
    contactId: string;
}

export interface IDeleteContactRequest {
    accountId: string;
    application: ApplicationEnum;
    contactId: string;
}
export interface ILastPatientMRNPayload {
    application: number;
    accountId: string;
}

export interface ILastPatientMRNResponse {
    LastUsedMRN: string;
}

export interface ILoadReferralParam {
    accountId: string;
    referralId: string;
    application: ApplicationEnum;
}

export interface IGetHospicePatientData {
    agencyId: string;
    patientId: string;
    application: ApplicationEnum;
}

export interface IGetEditHHReferral {
    referralId: string;
    agencyId: string;
    application: number;
}
export interface IGetVerifyEligibility {
    agencyId: string;
    medicareNumber: string;
    lastName: string;
    firstName: string;
    dob: string;
    gender: string;
}

export interface IGetHHPatientAllDataParam {
    agencyId: string;
    patientId: string;
}

export interface IGeneratePatientChart {
    patientId: string;
    agencyId: string;
    application: ApplicationEnum;
    referralId?: string;
}

export interface IGetCompletedPatientChart {
    referralId: string;
    agencyId: string;
    application: ApplicationEnum;
}

export interface IGetPatientChartStatus {
    agencyId: string;
    patientId: string;
    referralId: string;
    reportId: string;
    application: ApplicationEnum;
}

export interface IGetHCPatientData {
    agencyId: string;
    patientId: string;
    application: ApplicationEnum;
}

export interface IPatientDiagnosis {
    agencyId: string;
    application: ApplicationEnum;
    codeSearch: string;
    descriptionSearch: string;
}

export interface IPatientCalendarRequestParams {
    pageLength: number;
    page: number;
    term?: string;
    patientStatus?: number;
    agencies?: string[];
    locationIds?: string[];
    scheduleFilter?: number;
    startDate?: Date | string;
    endDate?: Date | string;
}

export interface IPatientProfileParams {
    agencyId: string;
    patientId: string;
    application: ApplicationEnum;
}

export interface IPaginationNew<T> {
    ItemCount: number;
    PageLength: number;
    CurrentPage: number;
    PageCount: number;
    Items: T[];
}

export interface IPatientCalendarResponse {
    Id: string;
    FirstName: string;
    LastName: string;
    Mrn: string;
    AgencyId: string;
    AgencyName: string;
    Application: number;
    PhoneNumber: string;
    MiddleInitial: string;
}

export interface IGetPatientsByNameQuery {
    sortBy: string;
    order: string;
    page: number;
    pageSize: number;
    term: string;
    locationIds?: string[];
}

export interface ICurrentEpisodeRequestParams {
    agencyId: string;
    patientId: string;
    application: ApplicationEnum;
}

export interface ICurrentEpisodeResponse {
    startDate: string;
    endDate: string;
    id: string;
    patientId: string;
    patientStatus: number;
    assessmentId: string;
    assessmentType: string;
    isActive: boolean;
    startOfCareDate: string;
}

export interface ICreateResponse {
    id: string;
}

export interface IGetOpenOrder {
    accountId: string;
    application: ApplicationEnum;
}

export interface IDownloadEligibilityPayload {
    PatientEligibility: {
        AgencyId: string;
        Request_Result: {
            success: string;
            response: string;
        };
        Request_Validation: {
            success: string;
        };
        Functional_Acknowledgment: {
            success: string;
        };
        Medicare_Part_A: {
            success: string;
            date: string;
            eligibility_or_benefit_information: string;
            insurance_type_code: string;
        };
        Medicare_Part_B: {
            success: string;
            date: string;
            eligibility_or_benefit_information: string;
            insurance_type_code: string;
        };
        Subscriber: {
            success: string;
            date: string;
            last_name: string;
            first_name: string;
            middle_name: string;
            identification_code: string;
            gender: string;
            expiration_date: Date | string;
            expiry_date: Date | string;
        };
        Dependent: {
            success: string;
        };
        Health_Benefit_Plan_Coverages: {
            success: string;
            healthBenefitPlanCoveragePayers: string[];
        };
        Episodes: {
            success: string;
            episodes: string[];
        };
        MedicareAdvantages: {
            success: string;
            medicareAdvantagePayers: [
                {
                    url: string;
                    payer: string;
                    name: string;
                    insurance_type: string;
                    reference_id_qualifier: string;
                    reference_id: string;
                    date: Date | string;
                    address1: string;
                    city: string;
                    state: string;
                    zip: string;
                    phone: string;
                }
            ];
        };
    };
    AgencyId: string;
}

export interface INearbyClientsPayload {
    Term: string;
    Distance: number;
    AgencyId: string;
    Application: ApplicationEnum;
    LocationId: string;
    StartDate: string;
    EndDate: string;
    CurrentDate: string;
    Page: number;
    PageLength: number;
    Latitude: number;
    Longitude: number;
}

export interface INearbyClient {
    Id: string;
    FirstName: string;
    MiddleInitial: string;
    LastName: string;
    Mrn: string;
    AgencyId: string;
    Application: ApplicationEnum;
    AgencyName: string;
    PhoneNumber: string;
    LocationId: string;
    Gender: GenderIdentityEnum;
    AddressLine1: string;
    AddressLine2: string;
    City: string;
    State: string;
    PostalCode: string;
    County: string;
    Latitude: number;
    Longitude: number;
    DOB: string;
    Status: PatientStatusEnum;
    Distance: number;
    marker?: any;
}

export interface INearbyClientsTask extends ITaskByPatientResponse {
    EvvIconStatus: EvvStatusHomeCareEnum | EvvStatusHomeHealthEnum;
    VerificationVisitStatus: EvvStatusHomeCareEnum | EvvStatusHomeHealthEnum;
}
export interface INearbyClientsResItem {
    PatientDetails: INearbyClient;
    PatientTask: INearbyClientsTask[];
    VisitFrequencyDay: number;
    VisitHoursDay: number;
    VisitFrequencyWeek: number;
    VisitHoursWeek: number;
    Distance: number;
}

export interface INearbyClientsRes {
    StartDate: string;
    EndDate: string;
    ItemCount: number;
    PageLength: number;
    CurrentPage: number;
    PageCount: number;
    Items: INearbyClientsResItem[];
}
